/**
 * Visa Constants Utility
 * Helper functions to work with visa criteria from visa-constants package
 */

// Cloudflare Workers (and some edge runtimes) don't provide CommonJS require.
// Explicitly import the ESM bundle to prevent Vite/wrangler from emitting a runtime `require(...)`.
// This avoids the "ReferenceError: require is not defined" seen in production.
// Use path without extension; ts will map types from package exports. Vite will pick ESM.
// Prefer package root import; handle environments without CommonJS by relying on ESM build selection.
// If bundler accidentally transforms to a require that fails at runtime, we can lazy-load via dynamic import fallback.
// Directly import from workspace source to avoid needing built dist during edge build.
// This ensures TypeScript sees the source and Vite bundles it without CommonJS require.
// Path correction: current file is apps/frontend/src/utils/visaConstants.ts -> go up 3 levels to repo root then packages.
import { VISA_CRITERIA_MAP, getCriteriaForVisa } from '../../../../packages/visa-constants/src/index';

/**
 * Get human-readable name for a criterion ID
 * @param criterionId - e.g. "eb1a_leading_role", "eb1a_high_salary"
 * @param visaType - e.g. "EB-1A", "O-1A"
 * @returns Human-readable name or the original ID if not found
 */
export function getCriterionName(criterionId: string, visaType?: string): string {
  // If visa type is provided, search within that visa type only
  if (visaType && VISA_CRITERIA_MAP[visaType]) {
    const criterion = VISA_CRITERIA_MAP[visaType].find((c: any) => c.id === criterionId);
    if (criterion) return criterion.name;
  }

  // Otherwise search across all visa types
  for (const criteria of Object.values(VISA_CRITERIA_MAP) as any[]) {
    const criterion = criteria.find((c: any) => c.id === criterionId);
    if (criterion) return criterion.name;
  }

  // Fallback: convert ID to human-readable format
  // e.g. "eb1a_leading_role" -> "Leading Role"
  return criterionId
    .split('_')
    .slice(1) // Remove visa type prefix (eb1a, o1a, etc.)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Build a map of criterion IDs to names for a specific visa type
 * @param visaType - e.g. "EB-1A", "O-1A"
 * @returns Map of criterion ID -> human-readable name
 */
export function buildCriteriaMap(visaType: string): Map<string, string> {
  const map = new Map<string, string>();
  const criteria = getCriteriaForVisa(visaType) as any[];

  if (Array.isArray(criteria)) {
    criteria.forEach((criterion: any) => {
      map.set(criterion.id, criterion.name);
    });
  }

  return map;
}

/**
 * Build a comprehensive map of all criteria across all visa types
 * @returns Map of criterion ID -> human-readable name
 */
export function buildAllCriteriaMap(): Map<string, string> {
  const map = new Map<string, string>();

  for (const [visaType, criteria] of Object.entries(VISA_CRITERIA_MAP) as [string, any[]][]) {
    if (Array.isArray(criteria)) {
      criteria.forEach((criterion: any) => {
        map.set(criterion.id, criterion.name);
      });
    }
  }

  return map;
}
